import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, Phone, Lock, ChevronRight } from "lucide-react";

export const IHPL_BLUE = "#004E9B";
export const IHPL_BLUE_DARK = "#003268";
export const IHPL_YELLOW = "#F5B301";
export const IHPL_RED = "#E11D2A";
export const IHPL_LOGO_URL = "/logos/ihpl.png";
export const INDOCG_LOGO_URL = "/logos/indocg.png";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/products", label: "Products" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/about", label: "About" },
];

export function IHPLLogo() {
  return (
    <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="ihpl-logo-link">
      <div className="bg-[#004E9B] rounded-md p-1.5 flex items-center justify-center" style={{ width: 52, height: 40 }}>
        <img src={IHPL_LOGO_URL} alt="Indus Hoses" className="w-full h-full object-contain" style={{ filter: "brightness(0) invert(1)" }} data-testid="ihpl-logo-img" />
      </div>
      <div className="leading-none">
        <div className="font-display font-black text-lg tracking-tight text-slate-900 uppercase">Indus Hoses</div>
        <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-slate-500 mt-1">Pvt Ltd · IHPL</div>
      </div>
    </Link>
  );
}

function EmergencyBar() {
  return (
    <div className="bg-[#E11D2A] text-white text-xs" data-testid="emergency-bar">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 h-9 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 font-medium">
          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
          <span className="uppercase tracking-wider">24/7 Emergency Hose Service</span>
        </div>
        <a href="tel:+918962913616" className="inline-flex items-center gap-1.5 font-semibold hover:underline" data-testid="emergency-bar-call">
          <Phone size={13} /> +91 89629 13616
        </a>
      </div>
    </div>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-slate-200" data-testid="public-header">
      <EmergencyBar />
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 h-20 flex items-center justify-between gap-6">
        <IHPLLogo />
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) => `px-4 py-2 text-sm font-semibold uppercase tracking-wide transition-colors ${isActive ? "text-[#004E9B]" : "text-slate-700 hover:text-[#004E9B]"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-2">
          <Link to="/login" data-testid="nav-staff-login"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider font-semibold text-slate-600 hover:text-[#004E9B] transition-colors">
            <Lock size={12} /> INDOCG Nexus Login
          </Link>
          <Link to="/contact" data-testid="nav-contact-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#004E9B] text-white font-semibold text-sm uppercase tracking-wider hover:bg-[#003268] transition-colors rounded-sm">
            Get a Quote <ArrowRight size={14} />
          </Link>
        </div>
        <button className="lg:hidden text-slate-900 p-2" onClick={() => setOpen(o => !o)} aria-label="menu" data-testid="nav-mobile-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200" data-testid="nav-mobile-panel">
          <div className="px-4 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                className={({ isActive }) => `py-3 px-4 font-semibold text-sm uppercase tracking-wide border-l-4 ${isActive ? "border-[#004E9B] text-[#004E9B] bg-slate-50" : "border-transparent text-slate-700"}`}>
                {n.label}
              </NavLink>
            ))}
            <Link to="/contact" className="mt-3 py-3 px-4 bg-[#004E9B] text-white font-semibold uppercase text-sm text-center rounded-sm" data-testid="nav-mobile-contact-cta">Get a Quote</Link>
            <Link to="/login" className="mt-2 py-3 px-4 border border-slate-300 text-slate-700 font-semibold uppercase text-sm text-center rounded-sm inline-flex items-center gap-2 justify-center" data-testid="nav-mobile-staff-login">
              <Lock size={14} /> INDOCG Nexus Login
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0A2540] text-white" data-testid="public-footer">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-14 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <div className="bg-white rounded-md p-1.5 flex items-center justify-center" style={{ width: 52, height: 40 }}>
              <img src={IHPL_LOGO_URL} alt="Indus Hoses" className="w-full h-full object-contain" />
            </div>
            <div className="leading-none">
              <div className="font-display font-black text-lg tracking-tight uppercase">Indus Hoses</div>
              <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-white/60 mt-1">Pvt Ltd · IHPL</div>
            </div>
          </div>
          <p className="mt-5 text-sm text-white/70 max-w-md leading-relaxed">
            India's trusted hydraulic hose assembly partner. Made in Prayagraj — dispatched to mines, mills, ports and heavy industry across UP, Jharkhand, Chhattisgarh, MP and Odisha.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <a href="tel:+918962913616" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#E11D2A] hover:bg-[#B01722] text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors" data-testid="footer-emergency">
              <Phone size={14} /> 24/7 Emergency
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-slate-900 hover:bg-slate-100 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors" data-testid="footer-quote">
              Get a Quote <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-white/50 mb-4">Explore</div>
          <ul className="space-y-2.5 text-sm">
            {NAV.concat({ to: "/contact", label: "Contact" }).map(n => (
              <li key={n.to}><Link to={n.to} className="text-white/80 hover:text-white flex items-center gap-1.5" data-testid={`footer-link-${n.label.toLowerCase()}`}><ChevronRight size={12} /> {n.label}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-white/50 mb-4">Head Office</div>
          <address className="not-italic text-sm text-white/80 leading-relaxed">
            Indus Hoses Pvt. Ltd.<br /> Prayagraj, Uttar Pradesh<br /> India<br /><br />
            <a href="mailto:sales@indushoses.co.in" className="hover:text-white">sales@indushoses.co.in</a><br />
            <a href="tel:+918962913616" className="hover:text-white">+91 89629 13616</a>
          </address>
          <Link to="/login" className="mt-5 inline-flex items-center gap-2 px-3 py-2 border border-white/20 hover:border-white text-white/80 hover:text-white text-xs font-semibold uppercase tracking-wider rounded-sm transition-colors" data-testid="footer-staff-login">
            <Lock size={12} /> INDOCG Nexus Login
          </Link>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/50">
          <span>© {new Date().getFullYear()} Indus Hoses Pvt. Ltd. All rights reserved.</span>
          <div className="flex items-center gap-3" data-testid="footer-indocg">
            <img src={INDOCG_LOGO_URL} alt="INDOCG Group" className="h-8 w-auto object-contain" style={{ filter: "brightness(0) invert(1)" }} />
            <div className="leading-none">
              <div className="font-bold uppercase text-sm">INDOCG Group</div>
              <div className="text-[10px] uppercase tracking-wider text-white/50 mt-0.5">Prayagraj · India</div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout({ children }) {
  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col" data-testid="public-layout">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
